import { useAppSelector, useAppDispatch } from "@/app/appStore";
import { TimelineRangeChangeParams } from "./timeline-controls";
import {
  selectSelectedOrder,
  selectTimelineRangeMinutes,
  setDateRange,
  updateTimelineRangeMinutes,
} from "@/entities/annotation";
import { useState, useEffect, useRef } from "react";
import { MAX_TIMELINE_RANGE, MIN_TIMELINE_RANGE } from "../lib/consts";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import { Box } from "@radix-ui/themes";
import { Input } from "@/components/input";

interface TimelineRangeInputsProps {
  onRangeChange: (params: TimelineRangeChangeParams) => void;
}

export function TimelineRangeInputs({
  onRangeChange,
}: TimelineRangeInputsProps) {
  const dispatch = useAppDispatch();
  const selectedOrder = useAppSelector(selectSelectedOrder);
  const timelineRange = useAppSelector(selectTimelineRangeMinutes);
  const [localRange, setLocalRange] = useState({
    beforeMinutes: timelineRange.beforeMinutes,
    afterMinutes: timelineRange.afterMinutes,
  });
  const skipBlurRef = useRef(false);

  // Keep local state in sync when Redux state changes from outside
  useEffect(() => {
    setLocalRange({
      beforeMinutes: timelineRange.beforeMinutes,
      afterMinutes: timelineRange.afterMinutes,
    });
  }, [timelineRange]);

  const validateTimelineRange = (range: {
    beforeMinutes: string;
    afterMinutes: string;
  }) => {
    const rangeBeforeMinutes = Number(range.beforeMinutes);
    const rangeAfterMinutes = Number(range.afterMinutes);

    if (rangeBeforeMinutes < MIN_TIMELINE_RANGE.beforeMinutes) {
      return `Timeline before range cannot be less than ${MIN_TIMELINE_RANGE.beforeMinutes} minutes`;
    }

    if (rangeAfterMinutes < MIN_TIMELINE_RANGE.afterMinutes) {
      return `Timeline after range cannot be less than ${MIN_TIMELINE_RANGE.afterMinutes} minutes`;
    }

    if (rangeBeforeMinutes > MAX_TIMELINE_RANGE.beforeMinutes) {
      return `Timeline before range cannot be greater than ${MAX_TIMELINE_RANGE.beforeMinutes} minutes`;
    }

    if (rangeAfterMinutes > MAX_TIMELINE_RANGE.afterMinutes) {
      return `Timeline after range cannot be greater than ${MAX_TIMELINE_RANGE.afterMinutes} minutes`;
    }

    return null;
  };

  const handleSetRange = () => {
    if (skipBlurRef.current) {
      skipBlurRef.current = false;
      return;
    }

    const errorMessage = validateTimelineRange(localRange);
    const { beforeMinutes, afterMinutes } = localRange;

    if (errorMessage) {
      toast.error(errorMessage);

      handleCancel();
      return;
    }

    const start = dayjs(selectedOrder?.time)
      .subtract(Number(beforeMinutes), "minute")
      .toDate();
    const end = dayjs(selectedOrder?.time)
      .add(Number(afterMinutes), "minute")
      .toDate();
    const needsUpdate =
      beforeMinutes.toString() !== timelineRange.beforeMinutes ||
      afterMinutes.toString() !== timelineRange.afterMinutes;

    if (needsUpdate) {
      dispatch(
        updateTimelineRangeMinutes({
          beforeMinutes: beforeMinutes.toString(),
          afterMinutes: afterMinutes.toString(),
        })
      );

      dispatch(setDateRange({ start, end }));

      onRangeChange({
        start,
        end,
        beforeMinutes: Number(beforeMinutes),
        afterMinutes: Number(afterMinutes),
      });
    }
  };

  const handleCancel = () => {
    setLocalRange({
      beforeMinutes: timelineRange.beforeMinutes,
      afterMinutes: timelineRange.afterMinutes,
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLocalRange((prev) => ({ ...prev, [name]: value }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "Tab") {
      handleSetRange();
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      skipBlurRef.current = true;
      handleCancel();
      e.currentTarget.blur();
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.select();
  };

  return (
    <div className="flex items-center gap-2">
      <Box>
        <Input
          type="number"
          name="beforeMinutes"
          label="Range Before(min)"
          className="h-10"
          value={localRange.beforeMinutes}
          floatingLabel={false}
          min={MIN_TIMELINE_RANGE.beforeMinutes.toString()}
          onChange={handleInputChange}
          onBlur={handleSetRange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
        />
      </Box>
      <Box>
        <Input
          type="number"
          name="afterMinutes"
          label="Range After(min)"
          className="h-10"
          value={localRange.afterMinutes}
          floatingLabel={false}
          min={MIN_TIMELINE_RANGE.afterMinutes.toString()}
          onChange={handleInputChange}
          onBlur={handleSetRange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
        />
      </Box>
    </div>
  );
}
